import * as React from "react";
import { useMemo } from "react";

import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import MantineProviderStatic from "@hl/base-components/lib/theme/MantineProviderStatic";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import DescriptionMarkdown from "@hl/shared-features/lib/features/markdown/DescriptionMarkdown";
import HighlightedNotes from "@hl/shared-features/lib/features/markdown/HighlightedNotes";
import {
  Text,
  Group,
  Stack,
  useMantineTheme,
  Flex,
  ScrollArea,
  Button,
  Title,
} from "@mantine/core";

import { TOOLS_URL } from "~config";
import CollectionCreatedBy from "~features/MintPage/CollectionCreatedBy";
import CollectionInfoHeaderMenu from "~features/MintPage/apollo/CollectionInfoHeader/CollectionInfoHeaderMenu";
import MarketplaceStatsHeader from "~features/MintPage/apollo/MarketplaceStatsHeader";
import { TagsCategoriesDetails } from "~features/MintPage/components/DetailSection/tags-categories/TagsCategoriesDetails";
import useMintState from "~hooks/useMintState";

type CardFooterProps = {
  imageUrl?: string | null;
};

const CollectionInfoHeader = ({ imageUrl }: CardFooterProps) => {
  const {
    collection,
    walletAddress,
    isImported,
    isSeriesMint,
    isCollectorChoiceMint,
    seriesTokenSelected,
    isCodeGenMint,
    tokens,
  } = useMintState();
  const title = collection?.name ?? "";
  const theme = useMantineTheme();
  const { isMedium, isMobile, isLarge } = useMantineMediaQueries({
    withInitialValues: true,
  });
  const defaultBannerImage = useMemo(() => {
    if (isImported) {
      return collection?.importData?.logoUrl ?? "";
    }
    if (isSeriesMint) {
      return isCollectorChoiceMint
        ? seriesTokenSelected?.imageUrl ?? ""
        : collection?.seriesImages?.coverImageUrl;
    }
    if (isCodeGenMint) {
      return collection?.generativeDetails?.logoUrl ?? "";
    }
    return tokens[0].image ?? "";
  }, [
    isSeriesMint,
    isCollectorChoiceMint,
    seriesTokenSelected,
    collection,
    tokens,
    isCodeGenMint,
  ]);

  const bannerImageUrl = collection?.bannerImageUrl || defaultBannerImage;
  const showBannerImage = !!bannerImageUrl;

  const detailsAndOwner = (
    <Flex align="center" gap={8} justify="flex-end">
      <Group align="center">
        {collection?.creatorAddresses?.some(
          (evmAddress) => evmAddress.address === walletAddress
        ) && (
          <>
            <Flex gap={8} justify="flex-end" sx={{ flexWrap: "wrap" }}>
              {!collection?.bannerImageUrl && (
                <Button
                  component="a"
                  size="xs"
                  href={`${TOOLS_URL.collections.manage.replace(
                    ":collectionId",
                    collection?.id ?? ""
                  )}?view=banner`}
                >
                  Add banner
                </Button>
              )}
              <Button
                component="a"
                color={SECONDARY_COLOR}
                size="xs"
                href={TOOLS_URL.collections.manage.replace(
                  ":collectionId",
                  collection.id
                )}
              >
                Manage collection
              </Button>
            </Flex>
          </>
        )}
      </Group>
      <MantineProviderStatic type={theme.colorScheme}>
        <CollectionInfoHeaderMenu hasBanner={showBannerImage} />
      </MantineProviderStatic>
    </Flex>
  );

  return (
    <Stack
      spacing={0}
      pt={40}
      sx={{
        borderBottom: `1px solid ${theme.colors.divider[0]}`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          inset: -40,
          background: showBannerImage
            ? `center / cover no-repeat url("${bannerImageUrl}")`
            : theme.colors.primaryBackground[0],
          filter: `blur(35px)`,
        },
      }}
      mx={isMobile ? -16 : -40}
    >
      {showBannerImage && (
        <>
          <Flex
            w="100%"
            h="100%"
            sx={{
              position: "absolute",
              bottom: 0,
              zIndex: 1,
              background:
                "linear-gradient(180deg, rgb(0% 0% 0% / 0) 0%, rgb(0% 0% 0% / 0.005284047889111632) 6.25%, rgb(0% 0% 0% / 0.02093312855939615) 12.5%, rgb(0% 0% 0% / 0.04634585661680007) 18.75%, rgb(0% 0% 0% / 0.08054563517369942) 25%, rgb(0% 0% 0% / 0.12221818591960938) 31.25%, rgb(0% 0% 0% / 0.1797620560996003) 37.5%, rgb(0% 0% 0% / 0.24135016144556474) 43.75%, rgb(0% 0% 0% / 0.29199999999999997) 50%, rgb(0% 0% 0% / 0.3446498385544353) 56.25%, rgb(0% 0% 0% / 0.39523794390039967) 62.5%, rgb(0% 0% 0% / 0.42778181408039057) 68.75%, rgb(0% 0% 0% / 0.4694543648263006) 75%, rgb(0% 0% 0% / 0.5036541433832) 81.25%, rgb(0% 0% 0% / 0.5290668714406039) 87.5%, rgb(0% 0% 0% / 0.5447159521108884) 93.75%, rgb(0% 0% 0% / 0.55) 100% )",
            }}
          ></Flex>
        </>
      )}
      <Flex
        pb={isMobile ? 16 : 32}
        sx={{ zIndex: 1 }}
        pt={!showBannerImage ? (isMobile ? 16 : 32) : undefined}
      >
        <MantineProviderStatic type="dark" isEnabled={showBannerImage}>
          <Flex
            justify="space-between"
            px={isMedium ? 40 : 20}
            w="100%"
            gap={16}
          >
            <Stack
              spacing={16}
              justify="flex-end"
              w={isLarge ? undefined : "100%"}
              sx={{ flex: isLarge ? 5 : undefined }}
            >
              <Flex justify="space-between">
                <Flex
                  gap="xs"
                  direction={isMobile ? "column" : "row"}
                  align={isMobile ? "start" : "center"}
                  w={isMobile ? "100%" : undefined}
                  pt={isMobile ? 0 : 60}
                >
                  <Flex
                    justify="space-between"
                    w={isMobile ? "100%" : undefined}
                  >
                    <ResizedImage
                      styles={{
                        root: {
                          width: "unset !important",
                        },
                      }}
                      src={imageUrl}
                      fit="contain"
                      height={isMobile ? 60 : 80}
                    />
                    {isMobile && detailsAndOwner}
                  </Flex>
                  <Stack spacing={0} maw={600}>
                    <Title lineClamp={1} order={isMobile ? 6 : 5}>
                      {title}
                    </Title>

                    <Flex justify="space-between" align="center" gap="xs">
                      <Text size={isMedium ? "xs" : 10}>
                        <CollectionCreatedBy numLines="1" />
                      </Text>
                    </Flex>
                  </Stack>
                </Flex>
                {!isMobile && !isLarge && (
                  <Flex align="flex-end">{detailsAndOwner}</Flex>
                )}
              </Flex>
              <DescriptionMarkdown
                description={collection?.description || ""}
                hasExpand={!collection?.flagVariations.increasedSidebarWidth}
                lineClamp={isMobile ? 3 : 1}
                numChars={300}
                hasLineClampExpanded={false}
              />
              <Flex justify="space-between" align="flex-end">
                <ScrollArea
                  type="never"
                  sx={{
                    marginLeft: isMobile ? -20 : -40,
                    marginRight: isMobile ? -20 : -40,
                  }}
                  styles={{
                    viewport: {
                      paddingLeft: isMobile ? 20 : 40,
                      paddingRight: isMobile ? 20 : 40,
                    },
                  }}
                >
                  <Stack>
                    <MarketplaceStatsHeader />
                    {collection?.id && (
                      <TagsCategoriesDetails
                        collectionId={collection?.id}
                        compact
                      />
                    )}
                  </Stack>
                </ScrollArea>
              </Flex>
              {collection?.editorial && !isLarge && (
                <HighlightedNotes
                  editorial={collection?.editorial}
                  numChars={256}
                />
              )}
            </Stack>
            {isLarge && (
              <Stack
                spacing={16}
                align="flex-end"
                justify="flex-end"
                sx={{ flex: 2, maxWidth: 400 }}
              >
                {collection?.editorial && isLarge && (
                  <HighlightedNotes
                    editorial={collection?.editorial}
                    numChars={192}
                  />
                )}
                {detailsAndOwner}
              </Stack>
            )}
          </Flex>
        </MantineProviderStatic>
      </Flex>
    </Stack>
  );
};

export default CollectionInfoHeader;
